import { LogOut } from "lucide-react";
import SiteBrandRow from "@/components/SiteBrandRow";
import { adminDisplayName, ROLE_LABEL, SALES_LEVEL_LABEL } from "@/lib/adminDisplay";

export const NAV_GROUPS = [
  { id: "overview", label: "OVERVIEW", tabIds: ["overview"] },
  { id: "sales", label: "SALES", tabIds: ["inquiries", "field-consultation", "sales-calculator", "sales-prices"] },
  { id: "quotes", label: "QUOTES", tabIds: ["quotes", "quote-notices"] },
  { id: "contracts", label: "CONTRACTS", tabIds: ["contracts", "contract-clauses"] },
  { id: "management", label: "MANAGEMENT", tabIds: ["delete-requests", "projects", "customers", "users"] },
  { id: "site", label: "SITE", tabIds: ["cms", "media", "portfolio", "site", "seo"] },
];

function initialsFromName(name) {
  const text = String(name || "").trim();
  if (!text) return "A";
  const first = Array.from(text)[0];
  return first ? first.toUpperCase() : "A";
}

export function adminRoleLabel(user) {
  if (!user) return "관리자";
  if (user.department === "sales") {
    return SALES_LEVEL_LABEL[user.sales_level] || "영업";
  }
  return ROLE_LABEL[user.role] || "관리자";
}

export default function AdminSidebar({
  tabs,
  activeTab,
  onSelect,
  user,
  cms,
  onLogout,
  newInquiryCount = 0,
  navRef,
  notification = null,
}) {
  const displayName = adminDisplayName(user);
  const roleLabel = adminRoleLabel(user);
  const tabById = Object.fromEntries((tabs || []).map((t) => [t.id, t]));
  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.tabIds.map((id) => tabById[id]).filter(Boolean),
  })).filter((group) => group.items.length > 0);

  return (
    <aside className="admin-sidebar" data-testid="admin-sidebar">
      <div className="admin-sidebar__brand">
        <SiteBrandRow
          cms={cms}
          linkTo="/"
          linkTestId="admin-logo-link"
          logoTestId="admin-logo-image"
          size="lg"
          iconOnly
          centered
        />
      </div>

      <div className="admin-sidebar__profile" data-testid="admin-sidebar-profile">
        <div className="admin-sidebar__avatar" aria-hidden="true">
          {initialsFromName(displayName)}
        </div>
        <div className="admin-sidebar__who">
          <div className="admin-sidebar__name">{displayName}</div>
          <div className="admin-sidebar__role">{roleLabel}</div>
        </div>
        <div className="admin-sidebar__bell">
          {notification}
        </div>
      </div>
      <button
        type="button"
        onClick={onLogout}
        data-testid="admin-logout"
        className="admin-sidebar__logout-text"
      >
        <LogOut size={12} aria-hidden="true" />
        로그아웃
      </button>

      <nav ref={navRef} className="admin-sidebar__nav" aria-label="관리자 메뉴">
        {groups.map((group) => (
          <div key={group.id} className="admin-sidebar__group">
            <div className="admin-sidebar__group-label">{group.label}</div>
            {group.items.map((t) => {
              const Icon = t.icon;
              const isActive = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onSelect(t.id)}
                  data-testid={`admin-tab-${t.id}`}
                  title={t.label}
                  className={`admin-sidebar__item ${
                    isActive ? "admin-sidebar__item--active" : "admin-sidebar__item--idle"
                  }`}
                >
                  <Icon size={16} aria-hidden="true" className="admin-sidebar__icon" />
                  <span className="admin-sidebar__item-label">{t.label}</span>
                  {t.id === "inquiries" && newInquiryCount > 0 ? (
                    <span className="admin-sidebar__badge">{newInquiryCount}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        ))}
      </nav>
    </aside>
  );
}
